// src/services/transactionImportService.js

import supabase from '../supabase/supabase';

/**
 * Transaction Import Service
 *
 * Flow:
 *
 * CSV File
 *   -> parseFile()
 *   -> parseCSV()
 *   -> detectColumns()
 *   -> normalizeRows()
 *   -> validateRows()
 *   -> createImportBatch()
 *   -> createImportTransactions()
 *   -> AI categorization
 *   -> review
 *   -> human approval
 *   -> approve-import Edge Function
 *   -> accounting.transactions
 *
 * IMPORTANT:
 * This service does NOT automatically approve or import
 * transactions during the Review phase.
 */

const SOURCE = 'csv';

const ACCOUNT_TYPE_MAP = {
  asset: 'Asset',
  assets: 'Asset',
  liability: 'Liability',
  liabilities: 'Liability',
  equity: 'Equity',
  equities: 'Equity',
  income: 'Income',
  revenue: 'Income',
  expense: 'Expenses',
  expenses: 'Expenses',
};

const normalizeHeader = (header = '') => {
  return String(header)
    .trim()
    .toLowerCase()
    .replace(/[\s\-\/]+/g, '_')
    .replace(/[^\w]/g, '');
};

const cleanString = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).trim();
};

const parseMoney = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  let text = String(value).trim();

  if (!text) {
    return 0;
  }

  let negative = false;

  // Accounting format: (123.45)
  if (text.startsWith('(') && text.endsWith(')')) {
    negative = true;
    text = text.slice(1, -1);
  }

  // Trailing minus: 123.45-
  if (text.endsWith('-')) {
    negative = true;
    text = text.slice(0, -1);
  }

  // Leading minus
  if (text.startsWith('-')) {
    negative = true;
    text = text.slice(1);
  }

  text = text
    .replace(/[$€£¥₱]/g, '')
    .replace(/,/g, '')
    .replace(/\s/g, '');

  const number = Number(text);

  if (!Number.isFinite(number)) {
    return 0;
  }

  return negative ? -Math.abs(number) : number;
};

const normalizeDateFormat = (dateFormat = 'auto') => {
  const formats = {
    'YYYY-MM-DD': 'yyyy-mm-dd',
    'MM/DD/YYYY': 'mm/dd/yyyy',
    'DD/MM/YYYY': 'dd/mm/yyyy',
    'MM/DD/YY': 'mm/dd/yy',
    'DD/MM/YY': 'dd/mm/yy',
  };

  return formats[dateFormat] || String(dateFormat).toLowerCase();
};

const isValidDate = (year, month, day) => {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return false;
  }

  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

const parseDate = (value, dateFormat = 'auto') => {
  if (!value) {
    return null;
  }

  const text = String(value).trim();

  if (!text) {
    return null;
  }

  const normalizedFormat = normalizeDateFormat(dateFormat);

  // Already ISO format.
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(text)) {
    const [year, month, day] = text.split('-').map(Number);

    return isValidDate(year, month, day)
      ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
          2,
          '0'
        )}`
      : null;
  }

  // YYYY/MM/DD
  if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(text)) {
    const [year, month, day] = text.split('/').map(Number);

    return isValidDate(year, month, day)
      ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
          2,
          '0'
        )}`
      : null;
  }

  const parts = text.split(/[\/\-\.]/).map((part) => part.trim());

  if (parts.length === 3) {
    const first = Number(parts[0]);
    const second = Number(parts[1]);
    const third = Number(parts[2]);

    if (
      Number.isFinite(first) &&
      Number.isFinite(second) &&
      Number.isFinite(third)
    ) {
      let year;
      let month;
      let day;

      if (normalizedFormat === 'yyyy-mm-dd') {
        year = first;
        month = second;
        day = third;
      } else if (normalizedFormat === 'dd-mm-yyyy') {
        day = first;
        month = second;
        year = third;
      } else if (normalizedFormat === 'mm-dd-yyyy') {
        month = first;
        day = second;
        year = third;
      } else if (normalizedFormat === 'dd/mm/yyyy') {
        day = first;
        month = second;
        year = third;
      } else if (normalizedFormat === 'mm/dd/yyyy') {
        month = first;
        day = second;
        year = third;
      } else if (normalizedFormat === 'dd/mm/yy') {
        day = first;
        month = second;
        year = 2000 + third;
      } else if (normalizedFormat === 'mm/dd/yy') {
        month = first;
        day = second;
        year = 2000 + third;
      } else {
        // Auto detect.
        if (first >= 1000) {
          year = first;
          month = second;
          day = third;
        } else if (third >= 1000 || third < 100) {
          year = third >= 1000 ? third : 2000 + third;

          if (first > 12) {
            day = first;
            month = second;
          } else if (second > 12) {
            month = first;
            day = second;
          } else {
            // Default ambiguous dates to MM/DD/YYYY.
            month = first;
            day = second;
          }
        }
      }

      if (isValidDate(year, month, day)) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
          2,
          '0'
        )}`;
      }
    }
  }

  return null;
};

/**
 * Small CSV parser.
 */
const parseCSVText = (text) => {
  const rows = [];
  let row = [];
  let value = '';
  let insideQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        value += '"';
        i += 1;
      } else {
        insideQuotes = !insideQuotes;
      }

      continue;
    }

    if (char === ',' && !insideQuotes) {
      row.push(value);
      value = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !insideQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i += 1;
      }

      row.push(value);
      value = '';

      if (row.some((cell) => cleanString(cell) !== '')) {
        rows.push(row);
      }

      row = [];
      continue;
    }

    value += char;
  }

  if (value !== '' || row.length > 0) {
    row.push(value);

    if (row.some((cell) => cleanString(cell) !== '')) {
      rows.push(row);
    }
  }

  return rows;
};

const detectDateFormatFromRows = (rows = [], mapping = {}) => {
  const dateIndex = mapping?.transaction_date;

  if (dateIndex === null || dateIndex === undefined) {
    return {
      format: 'MM/DD/YYYY',
      ambiguous: false,
    };
  }

  const samples = rows
    .slice(0, 20)
    .map((row) => cleanString(row?.[dateIndex]))
    .filter(Boolean);

  if (!samples.length) {
    return {
      format: 'MM/DD/YYYY',
      ambiguous: false,
    };
  }

  for (const sample of samples) {
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(sample)) {
      return {
        format: 'YYYY-MM-DD',
        ambiguous: false,
      };
    }

    const parts = sample.split(/[\/\-\.]/);

    if (parts.length !== 3) {
      continue;
    }

    const first = Number(parts[0]);
    const second = Number(parts[1]);
    const third = Number(parts[2]);

    if (first >= 1000) {
      return {
        format: 'YYYY-MM-DD',
        ambiguous: false,
      };
    }

    if (first > 12 && second <= 12) {
      return {
        format: third >= 1000 ? 'DD/MM/YYYY' : 'DD/MM/YY',
        ambiguous: false,
      };
    }

    if (second > 12 && first <= 12) {
      return {
        format: third >= 1000 ? 'MM/DD/YYYY' : 'MM/DD/YY',
        ambiguous: false,
      };
    }

    if (third >= 1000 || third < 100) {
      return {
        format: third >= 1000 ? 'MM/DD/YYYY' : 'MM/DD/YY',
        ambiguous: true,
      };
    }
  }

  return {
    format: 'MM/DD/YYYY',
    ambiguous: false,
  };
};

const getMappingIndex = (mapping, field) => {
  if (!mapping) {
    return null;
  }

  const value = mapping[field];

  if (value === null || value === undefined || value === '') {
    return null;
  }

  const index = Number(value);

  return Number.isInteger(index) && index >= 0 ? index : null;
};

const getMappedValue = (row, mapping, field) => {
  const index = getMappingIndex(mapping, field);

  if (index === null) {
    return '';
  }

  return cleanString(row?.[index]);
};

const detectColumnIndex = (headers, patterns, rows = []) => {
  const normalizedHeaders = headers.map((header) =>
    normalizeHeader(header)
  );

  for (const pattern of patterns) {
    const exactIndex = normalizedHeaders.findIndex(
      (header) => header === normalizeHeader(pattern)
    );

    if (exactIndex !== -1) {
      return exactIndex;
    }
  }

  for (const pattern of patterns) {
    const normalizedPattern = normalizeHeader(pattern);

    const containsIndex = normalizedHeaders.findIndex((header) =>
      header.includes(normalizedPattern)
    );

    if (containsIndex !== -1) {
      return containsIndex;
    }
  }

  const sampleIndex = headers.findIndex((_header, index) =>
    rows
      .slice(0, 20)
      .map((row) => cleanString(row?.[index]))
      .some(
        (value) =>
          /^\d{4}[-/.]\d{1,2}[-/.]\d{1,2}$/.test(value) ||
          /^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}$/.test(value)
      )
  );

  return sampleIndex === -1 ? null : sampleIndex;
};

const transactionImportService = {
  async parseFile(file) {
    if (!file) {
      throw new Error('No CSV file selected.');
    }

    if (!(file instanceof File)) {
      throw new Error('Invalid CSV file.');
    }

    const text = await file.text();

    if (!text || !text.trim()) {
      throw new Error('CSV file is empty.');
    }

    return this.parseCSV(text);
  },

  parseCSV(text) {
    if (typeof text !== 'string') {
      throw new Error(
        'CSV parser expected text. Use parseFile(file) when importing a File.'
      );
    }

    if (!text.trim()) {
      throw new Error('CSV file is empty.');
    }

    const cleanText = text.replace(/^\uFEFF/, '');

    const rawRows = parseCSVText(cleanText);

    if (!rawRows.length) {
      throw new Error('No data was found in the CSV file.');
    }

    const headers = rawRows[0].map((header) => cleanString(header));

    if (!headers.length || headers.every((header) => !header)) {
      throw new Error('CSV header row is missing.');
    }

    const rows = rawRows.slice(1);

    return {
      headers,
      rows,
      rowCount: rows.length,
    };
  },

  detectColumns(headers = [], rows = []) {
    if (!Array.isArray(headers)) {
      return {
        transaction_date: null,
        description: null,
        amount: null,
        debit: null,
        credit: null,
        balance: null,
        currency: null,
      };
    }

    const transaction_date = detectColumnIndex(
      headers,
      [
        'date',
        'transaction_date',
        'transactiondate',
        'transaction date',
        'trans_date',
        'trans date',
        'posting_date',
        'posting date',
        'post_date',
        'post date',
        'date_posted',
        'date posted',
        'effective_date',
        'effective date',
        'value_date',
        'value date',
      ],
      rows
    );

    const description = detectColumnIndex(headers, [
      'description',
      'transaction_description',
      'transactiondescription',
      'memo',
      'details',
      'narration',
      'payee',
      'merchant',
      'name',
    ]);

    const amount = detectColumnIndex(headers, [
      'amount',
      'transaction_amount',
      'transactionamount',
      'value',
    ]);

    const debit = detectColumnIndex(headers, [
      'debit',
      'debit_amount',
      'debitamount',
      'withdrawal',
      'withdrawals',
      'money_out',
      'moneyout',
    ]);

    const credit = detectColumnIndex(headers, [
      'credit',
      'credit_amount',
      'creditamount',
      'deposit',
      'deposits',
      'money_in',
      'moneyin',
    ]);

    const balance = detectColumnIndex(headers, [
      'balance',
      'running_balance',
      'runningbalance',
      'available_balance',
      'availablebalance',
    ]);

    const currency = detectColumnIndex(headers, [
      'currency',
      'currency_code',
      'currencycode',
      'ccy',
    ]);

    return {
      transaction_date,
      description,
      amount,
      debit,
      credit,
      balance,
      currency,
    };
  },

  detectDateFormat(rows = [], mapping = {}) {
    return detectDateFormatFromRows(rows, mapping);
  },

  validateMapping(mapping = {}) {
    const errors = [];

    if (getMappingIndex(mapping, 'transaction_date') === null) {
      errors.push('Transaction date column is required.');
    }

    if (getMappingIndex(mapping, 'description') === null) {
      errors.push('Description column is required.');
    }

    const amountIndex = getMappingIndex(mapping, 'amount');
    const debitIndex = getMappingIndex(mapping, 'debit');
    const creditIndex = getMappingIndex(mapping, 'credit');

    if (
      amountIndex === null &&
      debitIndex === null &&
      creditIndex === null
    ) {
      errors.push(
        'An Amount column or Debit/Credit columns are required.'
      );
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  },

  normalizeRow(
    row,
    mapping,
    options = {},
    rowNumber = 2
  ) {
    const {
      amountMode = 'signed',
      dateFormat = 'auto',
      currency = null,
    } = options;

    const transactionDateRaw = getMappedValue(
      row,
      mapping,
      'transaction_date'
    );

    const description = getMappedValue(
      row,
      mapping,
      'description'
    );

    const amountRaw = getMappedValue(
      row,
      mapping,
      'amount'
    );

    const debitRaw = getMappedValue(
      row,
      mapping,
      'debit'
    );

    const creditRaw = getMappedValue(
      row,
      mapping,
      'credit'
    );

    const balanceRaw = getMappedValue(
      row,
      mapping,
      'balance'
    );

    const currencyRaw = getMappedValue(
      row,
      mapping,
      'currency'
    );

    const transactionDate = parseDate(
      transactionDateRaw,
      dateFormat
    );

    const amount = parseMoney(amountRaw);

    const debitValue = Math.abs(
      parseMoney(debitRaw)
    );

    const creditValue = Math.abs(
      parseMoney(creditRaw)
    );

    let debit = 0;
    let credit = 0;

    if (amountRaw !== '') {
      if (amountMode === 'positive_debit') {
        if (amount >= 0) {
          debit = Math.abs(amount);
        } else {
          credit = Math.abs(amount);
        }
      } else if (amountMode === 'all_debit') {
        debit = Math.abs(amount);
      } else if (amountMode === 'all_credit') {
        credit = Math.abs(amount);
      } else if (amount < 0) {
        debit = Math.abs(amount);
      } else if (amount > 0) {
        credit = amount;
      }
    }

    /*
     * Explicit debit/credit columns override
     * the signed amount.
     */
    if (
      debitRaw !== '' ||
      creditRaw !== ''
    ) {
      debit = debitValue;
      credit = creditValue;
    }

    const normalized = {
      rowNumber,
      row_number: rowNumber,

      transaction_date:
        transactionDate,

      description,

      amount,

      debit,

      credit,

      balance:
        balanceRaw !== ''
          ? parseMoney(balanceRaw)
          : null,

      /*
       * Currency priority:
       *
       * 1. Currency column from CSV
       * 2. Currency supplied by organization context
       * 3. null
       *
       * We intentionally do NOT default
       * to USD here.
       */
      currency:
        currencyRaw ||
        currency ||
        null,

      raw_data: {},
    };

    if (Array.isArray(row)) {
      row.forEach((value, index) => {
        normalized.raw_data[String(index)] =
          value;
      });
    }

    return normalized;
  },

  normalizeRows(
    input,
    mappingArg = {},
    optionsArg = {}
  ) {
    let rows;
    let mapping;
    let options;

    if (
      input &&
      typeof input === 'object' &&
      !Array.isArray(input) &&
      Array.isArray(input.rows)
    ) {
      rows = input.rows;

      mapping =
        input.mapping || {};

      options = {
        amountMode:
          input.amountMode ||
          'signed',

        dateFormat:
          input.dateFormat ||
          'auto',

        currency:
          input.currency ||
          null,
      };
    } else {
      rows = Array.isArray(input)
        ? input
        : [];

      mapping =
        mappingArg || {};

      options =
        optionsArg || {};
    }

    return rows.map(
      (row, index) =>
        this.normalizeRow(
          row,
          mapping,
          options,
          index + 2
        )
    );
  },

  validateRows(rows = []) {
    const validRows = [];
    const invalidRows = [];

    rows.forEach((row) => {
      const errors = [];

      if (!row.transaction_date) {
        errors.push(
          'Invalid or missing date.'
        );
      }

      if (!row.description) {
        errors.push(
          'Missing description.'
        );
      }

      if (
        Number(row.debit || 0) === 0 &&
        Number(row.credit || 0) === 0
      ) {
        errors.push(
          'Amount is zero or missing.'
        );
      }

      if (
        Number(row.debit || 0) < 0 ||
        Number(row.credit || 0) < 0
      ) {
        errors.push(
          'Debit and credit cannot be negative.'
        );
      }

      if (
        Number(row.debit || 0) > 0 &&
        Number(row.credit || 0) > 0
      ) {
        errors.push(
          'A transaction cannot have both debit and credit.'
        );
      }

      if (errors.length) {
        invalidRows.push({
          ...row,

          rowNumber:
            row.rowNumber ||
            row.row_number,

          row_number:
            row.row_number ||
            row.rowNumber,

          error:
            errors[0],

          errors,
        });
      } else {
        validRows.push(row);
      }
    });

    return {
      valid: validRows,
      invalid: invalidRows,

      validRows,
      invalidRows,

      total: rows.length,

      validCount:
        validRows.length,

      invalidCount:
        invalidRows.length,
    };
  },

  /**
   * Get active accounting accounts
   * for one organization.
   *
   * Accounts are always scoped to
   * organizationId.
   */
  async getAccounts(
    organizationId
  ) {
    if (!organizationId) {
      throw new Error(
        'Organization ID is required.'
      );
    }

    const {
      data,
      error,
    } = await supabase
      .schema('accounting')
      .from('accounts')
      .select(`
        id,
        organization_id,
        code,
        name,
        type,
        sub_type,
        description,
        currency,
        is_active
      `)
      .eq(
        'organization_id',
        organizationId
      )
      .eq(
        'is_active',
        true
      )
      .order('code', {
        ascending: true,
        nullsFirst: false,
      })
      .order('name', {
        ascending: true,
      });

    if (error) {
      console.error(
        '[transactionImportService] getAccounts error:',
        error
      );

      throw error;
    }

    return data || [];
  },

  /**
   * Review modal account loader.
   */
  async getImportAccounts(
    organizationId
  ) {
    return this.getAccounts(
      organizationId
    );
  },

  /**
   * Create import batch.
   */
  async createImportBatch({
    organizationId,
    fileName,
    totalRows,
    source = SOURCE,
    createdBy = null,
  }) {
    if (!organizationId) {
      throw new Error(
        'Organization ID is required.'
      );
    }

    const payload = {
      organization_id:
        organizationId,

      file_name:
        fileName ||
        'import.csv',

      source,

      total_rows:
        Number(totalRows || 0),

      processed_rows: 0,

      categorized_rows: 0,

      review_rows: 0,

      duplicate_rows: 0,

      imported_rows: 0,

      status: 'uploaded',

      created_by:
        createdBy,
    };

    const {
      data,
      error,
    } = await supabase
      .schema('accounting')
      .from('import_batches')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      console.error(
        '[transactionImportService] createImportBatch error:',
        error
      );

      throw error;
    }

    return data;
  },

  /**
   * Create staging rows.
   *
   * Every row is tied to:
   *
   * organization_id
   * import_batch_id
   * source_account_id
   */
  async createImportTransactions({
    organizationId,
    importBatchId,
    rows,
    sourceAccountId,
  }) {
    if (!organizationId) {
      throw new Error(
        'Organization ID is required.'
      );
    }

    if (!importBatchId) {
      throw new Error(
        'Import batch ID is required.'
      );
    }

    if (!sourceAccountId) {
      throw new Error(
        'Please select the account this CSV statement belongs to.'
      );
    }

    if (
      !Array.isArray(rows) ||
      !rows.length
    ) {
      throw new Error(
        'There are no valid rows to import.'
      );
    }

    /*
     * Verify source account belongs
     * to this organization.
     */
    const {
      data: sourceAccount,
      error: sourceError,
    } = await supabase
      .schema('accounting')
      .from('accounts')
      .select(
        'id, organization_id, is_active'
      )
      .eq(
        'id',
        sourceAccountId
      )
      .eq(
        'organization_id',
        organizationId
      )
      .eq(
        'is_active',
        true
      )
      .maybeSingle();

    if (sourceError) {
      console.error(
        '[transactionImportService] createImportTransactions source account error:',
        sourceError
      );

      throw sourceError;
    }

    if (!sourceAccount) {
      throw new Error(
        'The selected source account does not belong to this organization.'
      );
    }

    /*
     * Verify batch belongs to
     * this organization.
     */
    const {
      data: batch,
      error: batchError,
    } = await supabase
      .schema('accounting')
      .from('import_batches')
      .select(
        'id, organization_id'
      )
      .eq(
        'id',
        importBatchId
      )
      .eq(
        'organization_id',
        organizationId
      )
      .maybeSingle();

    if (batchError) {
      console.error(
        '[transactionImportService] createImportTransactions batch error:',
        batchError
      );

      throw batchError;
    }

    if (!batch) {
      throw new Error(
        'Import batch not found for this organization.'
      );
    }

    const payload =
      rows.map(
        (row, index) => ({
          import_batch_id:
            importBatchId,

          organization_id:
            organizationId,

          row_number:
            Number(
              row.row_number ||
                row.rowNumber ||
                index + 2
            ),

          transaction_date:
            row.transaction_date,

          description:
            row.description ||
            '',

          amount:
            Number(
              row.amount || 0
            ),

          debit:
            Number(
              row.debit || 0
            ),

          credit:
            Number(
              row.credit || 0
            ),

          currency:
            row.currency ||
            null,

          raw_data:
            row.raw_data ||
            {},

          source_account_id:
            sourceAccountId,

          suggested_account_id:
            null,

          confidence:
            null,

          ai_reason:
            null,

          status:
            'pending',

          is_duplicate:
            false,
        })
      );

    const {
      data,
      error,
    } = await supabase
      .schema('accounting')
      .from('import_transactions')
      .insert(payload)
      .select('*');

    if (error) {
      console.error(
        '[transactionImportService] createImportTransactions insert error:',
        error
      );

      throw error;
    }

    return data || [];
  },

  /**
   * Stage the CSV import.
   */
  async stageImport({
    organizationId,
    fileName,
    rows,
    sourceAccountId,
    createdBy = null,
  }) {
    if (!organizationId) {
      throw new Error(
        'Organization ID is required.'
      );
    }

    const validation =
      this.validateRows(rows);

    if (
      validation.invalid.length
    ) {
      const firstError =
        validation.invalid[0]
          ?.errors
          ?.join(' ') ||
        'Some CSV rows are invalid.';

      throw new Error(
        firstError
      );
    }

    const batch =
      await this.createImportBatch({
        organizationId,
        fileName,
        totalRows:
          validation.valid.length,
        source: SOURCE,
        createdBy,
      });

    try {
      await supabase
        .schema('accounting')
        .from('import_batches')
        .update({
          status:
            'processing',

          updated_at:
            new Date().toISOString(),
        })
        .eq(
          'id',
          batch.id
        )
        .eq(
          'organization_id',
          organizationId
        );

      const importRows =
        await this.createImportTransactions({
          organizationId,
          importBatchId:
            batch.id,
          rows:
            validation.valid,
          sourceAccountId,
        });

      await supabase
        .schema('accounting')
        .from('import_batches')
        .update({
          processed_rows: 0,

          categorized_rows: 0,

          review_rows: 0,

          duplicate_rows: 0,

          imported_rows: 0,

          status:
            'processing',

          updated_at:
            new Date().toISOString(),
        })
        .eq(
          'id',
          batch.id
        )
        .eq(
          'organization_id',
          organizationId
        );

      return {
        batch,

        importRows,

        validRows:
          validation.valid,

        invalidRows:
          validation.invalid,
      };
    } catch (error) {
      await supabase
        .schema('accounting')
        .from('import_batches')
        .update({
          status:
            'failed',

          error_message:
            error?.message ||
            'Import staging failed.',

          updated_at:
            new Date().toISOString(),
        })
        .eq(
          'id',
          batch.id
        )
        .eq(
          'organization_id',
          organizationId
        );

      throw error;
    }
  },

 /**
 * Get Review Import Transactions data.
 *
 * Supports both:
 *
 * CSV:
 * getImportReviewData(
 *   organizationId,
 *   importBatchId
 * )
 *
 * Bank/Plaid:
 * getImportReviewData(
 *   organizationId,
 *   importBatchId,
 *   sourceBankAccountId
 * )
 *
 * IMPORTANT:
 *
 * sourceBankAccountId is the ID from:
 *
 * accounting.bank_accounts.id
 *
 * It is NOT:
 *
 * accounting.accounts.id
 *
 * The bank account is resolved to its mapped
 * accounting.accounts.id through:
 *
 * bank_accounts.account_id
 *
 * CSV behavior is unchanged when the third
 * argument is omitted.
 */
  async getImportReviewData(
    organizationIdOrOptions,
    importBatchIdArg,
    sourceBankAccountIdArg = null
  ) {
    let organizationId;
    let importBatchId;
    let sourceBankAccountId = null;

    /*
    * Supports:
    *
    * getImportReviewData(
    *   organizationId,
    *   importBatchId
    * )
    *
    * getImportReviewData(
    *   organizationId,
    *   importBatchId,
    *   sourceBankAccountId
    * )
    *
    * and:
    *
    * getImportReviewData({
    *   organizationId,
    *   importBatchId,
    *   sourceBankAccountId
    * })
    */
    if (
      organizationIdOrOptions &&
      typeof organizationIdOrOptions === 'object'
    ) {
      organizationId =
        organizationIdOrOptions.organizationId;

      importBatchId =
        organizationIdOrOptions.importBatchId;

      sourceBankAccountId =
        organizationIdOrOptions.sourceBankAccountId ||
        organizationIdOrOptions.source_bank_account_id ||
        null;
    } else {
      organizationId =
        organizationIdOrOptions;

      importBatchId =
        importBatchIdArg;

      sourceBankAccountId =
        sourceBankAccountIdArg || null;
    }

    if (!organizationId) {
      throw new Error(
        'Organization ID is required.'
      );
    }

    if (!importBatchId) {
      throw new Error(
        'Import batch ID is required.'
      );
    }

    /*
    * -------------------------------------------------------
    * 1. Load the import batch.
    *
    * Always scope by:
    *
    * id
    * +
    * organization_id
    *
    * This applies to BOTH CSV and bank imports.
    * -------------------------------------------------------
    */
    const {
      data: batch,
      error: batchError,
    } = await supabase
      .schema('accounting')
      .from('import_batches')
      .select('*')
      .eq(
        'id',
        importBatchId
      )
      .eq(
        'organization_id',
        organizationId
      )
      .maybeSingle();

    if (batchError) {
      console.error(
        '[transactionImportService] getImportReviewData batch error:',
        {
          organizationId,
          importBatchId,
          error: batchError,
        }
      );

      throw new Error(
        `Failed to load import batch: ${
          batchError.message ||
          'Unknown database error.'
        }`
      );
    }

    if (!batch) {
      throw new Error(
        'Import batch not found for this organization.'
      );
    }

    /*
    * -------------------------------------------------------
    * 2. Resolve selected bank account.
    *
    * This section ONLY runs when a bank account ID
    * was supplied.
    *
    * Therefore:
    *
    * CSV calls are completely unaffected.
    *
    * The bank account must:
    *
    * - exist
    * - belong to organization
    * - be active
    * - have an accounting account mapping
    * -------------------------------------------------------
    */

    let selectedBankAccount = null;
    let selectedSourceAccountId = null;

    if (sourceBankAccountId) {
      const {
        data: bankAccount,
        error: bankAccountError,
      } = await supabase
        .schema('accounting')
        .from('bank_accounts')
        .select(`
          id,
          organization_id,
          account_id,
          institution_name,
          account_name,
          account_type,
          mask,
          plaid_item_id,
          plaid_account_id,
          is_active
        `)
        .eq(
          'id',
          sourceBankAccountId
        )
        .eq(
          'organization_id',
          organizationId
        )
        .eq(
          'is_active',
          true
        )
        .maybeSingle();

      if (bankAccountError) {
        console.error(
          '[transactionImportService] getImportReviewData bank account error:',
          {
            organizationId,
            importBatchId,
            sourceBankAccountId,
            error: bankAccountError,
          }
        );

        throw new Error(
          `Failed to load selected bank account: ${
            bankAccountError.message ||
            'Unknown database error.'
          }`
        );
      }

      if (!bankAccount) {
        throw new Error(
          'The selected bank account does not belong to this organization or is inactive.'
        );
      }

      if (!bankAccount.account_id) {
        throw new Error(
          'The selected bank account is not mapped to an accounting account.'
        );
      }

      selectedBankAccount =
        bankAccount;

      selectedSourceAccountId =
        bankAccount.account_id;
    }

    /*
    * -------------------------------------------------------
    * 3. Load import transactions.
    *
    * ALWAYS scoped by:
    *
    * organization_id
    * +
    * import_batch_id
    *
    * For CSV:
    *
    * sourceBankAccountId = null
    * -> no additional filter
    *
    * For Bank/Plaid:
    *
    * sourceBankAccountId supplied
    * -> filter by the mapped accounting
    *    source account.
    * -------------------------------------------------------
    */

    let rowsQuery =
      supabase
        .schema('accounting')
        .from('import_transactions')
        .select(`
          id,
          import_batch_id,
          organization_id,
          row_number,
          transaction_date,
          description,
          amount,
          debit,
          credit,
          currency,
          raw_data,
          source_account_id,
          suggested_account_id,
          confidence,
          ai_reason,
          status,
          is_duplicate
        `)
        .eq(
          'organization_id',
          organizationId
        )
        .eq(
          'import_batch_id',
          importBatchId
        );

    /*
    * IMPORTANT:
    *
    * source_account_id in import_transactions
    * contains accounting.accounts.id.
    *
    * bank_accounts.id is resolved above to:
    *
    * bank_accounts.account_id
    *
    * We therefore filter using:
    *
    * source_account_id =
    * selectedBankAccount.account_id
    */
    if (selectedSourceAccountId) {
      rowsQuery =
        rowsQuery.eq(
          'source_account_id',
          selectedSourceAccountId
        );
    }

    const {
      data: rawRows,
      error: rowsError,
    } =
      await rowsQuery.order(
        'row_number',
        {
          ascending: true,
        }
      );

    if (rowsError) {
      console.error(
        '[transactionImportService] getImportReviewData rows error:',
        {
          organizationId,
          importBatchId,
          sourceBankAccountId,
          selectedSourceAccountId,
          error: rowsError,
          message:
            rowsError.message,
          code:
            rowsError.code,
          details:
            rowsError.details,
          hint:
            rowsError.hint,
        }
      );

      throw new Error(
        `Failed to load import transactions: ${
          rowsError.message ||
          'Unknown database error.'
        }`
      );
    }

    const rows = Array.isArray(
      rawRows
    )
      ? rawRows
      : [];

    /*
    * -------------------------------------------------------
    * 4. Collect accounting account IDs.
    *
    * source_account_id
    * +
    * suggested_account_id
    * -------------------------------------------------------
    */
    const accountIds = [
      ...new Set(
        rows
          .flatMap(
            (row) => [
              row.source_account_id,
              row.suggested_account_id,
            ]
          )
          .filter(Boolean)
      ),
    ];

    let accountMap =
      new Map();

    /*
    * -------------------------------------------------------
    * 5. Load related accounting accounts.
    *
    * ALWAYS organization scoped.
    *
    * This prevents account information from another
    * organization from being attached to the rows.
    * -------------------------------------------------------
    */
    if (accountIds.length > 0) {
      const {
        data: accounts,
        error: accountsError,
      } = await supabase
        .schema('accounting')
        .from('accounts')
        .select(`
          id,
          organization_id,
          code,
          name,
          type,
          sub_type,
          description,
          currency,
          is_active
        `)
        .eq(
          'organization_id',
          organizationId
        )
        .in(
          'id',
          accountIds
        );

      if (accountsError) {
        console.error(
          '[transactionImportService] getImportReviewData accounts error:',
          {
            organizationId,
            importBatchId,
            accountIds,
            error: accountsError,
            message:
              accountsError.message,
            code:
              accountsError.code,
            details:
              accountsError.details,
            hint:
              accountsError.hint,
          }
        );

        throw new Error(
          `Failed to load import accounts: ${
            accountsError.message ||
            'Unknown database error.'
          }`
        );
      }

      accountMap =
        new Map(
          (accounts || []).map(
            (account) => [
              account.id,
              account,
            ]
          )
        );
    }

    /*
    * -------------------------------------------------------
    * 6. Hydrate transaction rows.
    *
    * sourceAccount:
    *   Bank/card accounting account
    *
    * suggestedAccount:
    *   AI/human-selected accounting category
    *
    * These remain separate.
    * -------------------------------------------------------
    */
    const hydratedRows =
      rows.map(
        (row) => ({
          ...row,

          sourceAccount:
            accountMap.get(
              row.source_account_id
            ) || null,

          suggestedAccount:
            accountMap.get(
              row.suggested_account_id
            ) || null,

          /*
          * Include the selected bank account ID
          * when this is a bank review.
          *
          * This is additional metadata only.
          * It does not modify the database row.
          */
          sourceBankAccountId:
            sourceBankAccountId ||
            null,
        })
      );

    /*
    * -------------------------------------------------------
    * 7. Logging
    * -------------------------------------------------------
    */
    console.log(
      '[transactionImportService] Review data loaded:',
      {
        organizationId,
        importBatchId,

        /*
        * null means this was the normal CSV/all-batch
        * behavior.
        */
        sourceBankAccountId:
          sourceBankAccountId ||
          null,

        selectedSourceAccountId:
          selectedSourceAccountId ||
          null,

        rowCount:
          hydratedRows.length,

        source:
          batch?.source ||
          null,
      }
    );

    /*
    * -------------------------------------------------------
    * 8. Return.
    *
    * Existing CSV callers still receive:
    *
    * {
    *   batch,
    *   rows
    * }
    *
    * Bank callers additionally receive:
    *
    * selectedBankAccount
    * -------------------------------------------------------
    */
    return {
      batch,

      rows:
        hydratedRows,

      selectedBankAccount,
    };
  },

  /**
   * Update the human-selected
   * suggested accounting account.
   *
   * Preserves:
   *
   * - confidence
   * - ai_reason
   * - source_account_id
   *
   * No new database column is required.
   */
  async updateSuggestedAccount({
    organizationId,
    importBatchId,
    importTransactionId,
    suggestedAccountId,
  }) {
    if (
      !organizationId ||
      !importBatchId ||
      !importTransactionId ||
      !suggestedAccountId
    ) {
      throw new Error(
        'Organization, import batch, transaction, and suggested account are required.'
      );
    }

    /*
     * Verify the suggested account:
     *
     * - exists
     * - belongs to this organization
     * - is active
     */
    const {
      data: account,
      error: accountError,
    } = await supabase
      .schema('accounting')
      .from('accounts')
      .select(`
        id,
        organization_id,
        code,
        name,
        type,
        sub_type,
        description,
        currency,
        is_active
      `)
      .eq(
        'id',
        suggestedAccountId
      )
      .eq(
        'organization_id',
        organizationId
      )
      .eq(
        'is_active',
        true
      )
      .maybeSingle();

    if (accountError) {
      console.error(
        '[transactionImportService] updateSuggestedAccount account error:',
        accountError
      );

      throw accountError;
    }

    if (!account) {
      throw new Error(
        'The selected accounting account does not belong to this organization or is inactive.'
      );
    }

    /*
     * Verify the import row belongs to:
     *
     * organization
     * +
     * batch
     */
    const {
      data: existingRow,
      error: rowError,
    } = await supabase
      .schema('accounting')
      .from('import_transactions')
      .select(`
        id,
        organization_id,
        import_batch_id,
        suggested_account_id,
        confidence,
        ai_reason,
        source_account_id,
        status,
        is_duplicate
      `)
      .eq(
        'id',
        importTransactionId
      )
      .eq(
        'import_batch_id',
        importBatchId
      )
      .eq(
        'organization_id',
        organizationId
      )
      .maybeSingle();

    if (rowError) {
      console.error(
        '[transactionImportService] updateSuggestedAccount row error:',
        rowError
      );

      throw rowError;
    }

    if (!existingRow) {
      throw new Error(
        'The import transaction could not be found in this organization and batch.'
      );
    }

    /*
     * Do not allow a duplicate row to
     * be manually recategorized.
     */
    if (
      existingRow.status === 'duplicate' ||
      existingRow.is_duplicate
    ) {
      throw new Error(
        'Duplicate transactions cannot be recategorized during Review.'
      );
    }

    /*
     * Never allow a suggested category
     * to become the same account as the
     * source account.
     *
     * Source account:
     * bank/card account the CSV came from.
     *
     * Suggested account:
     * accounting category selected by
     * AI or human.
     */
    if (
      existingRow.source_account_id &&
      account.id ===
        existingRow.source_account_id
    ) {
      throw new Error(
        'The source bank/card account cannot be used as the suggested accounting category.'
      );
    }

    /*
     * Do not modify transactions that
     * have already been approved/imported.
     */
    if (
      existingRow.status ===
        'approved' ||
      existingRow.status ===
        'imported'
    ) {
      throw new Error(
        'This transaction has already been approved and cannot be recategorized.'
      );
    }

    /*
     * Manual review override.
     *
     * Preserve:
     *
     * confidence
     * ai_reason
     * source_account_id
     *
     * We only change:
     *
     * suggested_account_id
     * status
     * updated_at
     */
    const {
      data: updatedRow,
      error: updateError,
    } = await supabase
      .schema('accounting')
      .from('import_transactions')
      .update({
        suggested_account_id:
          suggestedAccountId,

        status:
          'categorized',

        updated_at:
          new Date().toISOString(),
      })
      .eq(
        'id',
        importTransactionId
      )
      .eq(
        'import_batch_id',
        importBatchId
      )
      .eq(
        'organization_id',
        organizationId
      )
      .select('*')
      .single();

    if (updateError) {
      console.error(
        '[transactionImportService] updateSuggestedAccount update error:',
        updateError
      );

      throw updateError;
    }

    return {
      ...updatedRow,

      suggestedAccount:
        account,
    };
  },

  /**
   * Get current categorization status.
   */
  async getCategorizationStatus({
    organizationId,
    importBatchId,
  }) {
    if (
      !organizationId ||
      !importBatchId
    ) {
      throw new Error(
        'Organization ID and import batch ID are required.'
      );
    }

    const {
      data,
      error,
    } = await supabase
      .schema('accounting')
      .from('import_transactions')
      .select(`
        id,
        status,
        suggested_account_id,
        confidence,
        is_duplicate
      `)
      .eq(
        'organization_id',
        organizationId
      )
      .eq(
        'import_batch_id',
        importBatchId
      );

    if (error) {
      console.error(
        '[transactionImportService] getCategorizationStatus error:',
        error
      );

      throw error;
    }

    const rows =
      data || [];

    const total =
      rows.length;

    const categorized =
      rows.filter(
        (row) =>
          row.status ===
            'categorized' ||
          row.status ===
            'approved' ||
          row.status ===
            'imported'
      ).length;

    const needsReview =
      rows.filter(
        (row) =>
          row.status ===
          'needs_review'
      ).length;

    const duplicates =
      rows.filter(
        (row) =>
          row.status ===
            'duplicate' ||
          row.is_duplicate
      ).length;

    const pending =
      rows.filter(
        (row) =>
          row.status ===
          'pending'
      ).length;

    return {
      total,

      categorized,

      needsReview,

      duplicates,

      pending,

      percentage:
        total > 0
          ? Math.round(
              (categorized /
                total) *
                100
            )
          : 0,
    };
  },

  /**
   * Validate whether the current batch
   * is ready for approval.
   *
   * This does NOT approve anything.
   *
   * Approval remains a separate workflow.
   */
  async validateReadyForApproval({
    organizationId,
    importBatchId,
  }) {
    const {
      rows,
    } =
      await this.getImportReviewData(
        organizationId,
        importBatchId
      );

    const activeRows =
      rows.filter(
        (row) =>
          !row.is_duplicate &&
          row.status !==
            'duplicate'
      );

    const missingCategoryRows =
      activeRows.filter(
        (row) =>
          !row.suggested_account_id
      );

    const invalidRows =
      activeRows.filter(
        (row) =>
          ![
            'categorized',
            'approved',
            'imported',
          ].includes(
            row.status
          )
      );

    return {
      valid:
        missingCategoryRows.length ===
          0 &&
        invalidRows.length ===
          0,

      totalRows:
        activeRows.length,

      missingCategoryRows,

      invalidRows,
    };
  },

  /**
   * APPROVAL / IMPORT
   *
   * Kept for the later workflow.
   *
   * The Review UI must NOT call this
   * until Review testing is complete.
   *
   * The Edge Function remains responsible
   * for final approval/import.
   */
  async approveImport({
    organizationId,
    importBatchId,
    mode = 'all',
    importTransactionIds = [],
  }) {
    if (
      !organizationId ||
      !importBatchId
    ) {
      throw new Error(
        'Organization and import batch are required.'
      );
    }

    if (
      !['selected', 'all'].includes(
        mode
      )
    ) {
      throw new Error(
        'Approval mode must be "selected" or "all".'
      );
    }

    if (
      mode === 'selected' &&
      (!Array.isArray(
        importTransactionIds
      ) ||
        importTransactionIds.length ===
          0)
    ) {
      throw new Error(
        'At least one transaction must be selected for approval.'
      );
    }

    const body = {
      organizationId,

      importBatchId,

      mode,

      importTransactionIds:
        mode === 'selected'
          ? importTransactionIds
          : [],
    };

    const {
      data,
      error,
    } =
      await supabase.functions.invoke(
        'approve-import',
        {
          body,
        }
      );

    if (error) {
      console.error(
        '[transactionImportService] approveImport error:',
        error
      );

      throw error;
    }

    if (!data?.success) {
      const message =
        data?.error ||
        data?.message ||
        'The transactions could not be approved.';

      throw new Error(
        message
      );
    }

    return data;
  },

  /**
   * Cancel/delete an import batch.
   *
   * Still scoped to organization.
   */
  async cancelImport({
    organizationId,
    importBatchId,
  }) {
    if (
      !organizationId ||
      !importBatchId
    ) {
      throw new Error(
        'Organization ID and import batch ID are required.'
      );
    }

    /*
     * Delete staging rows first.
     */
    const {
      error: rowsError,
    } = await supabase
      .schema('accounting')
      .from('import_transactions')
      .delete()
      .eq(
        'organization_id',
        organizationId
      )
      .eq(
        'import_batch_id',
        importBatchId
      );

    if (rowsError) {
      console.error(
        '[transactionImportService] cancelImport rows error:',
        rowsError
      );

      throw rowsError;
    }

    /*
     * Then delete batch.
     */
    const {
      error: batchError,
    } = await supabase
      .schema('accounting')
      .from('import_batches')
      .delete()
      .eq(
        'organization_id',
        organizationId
      )
      .eq(
        'id',
        importBatchId
      );

    if (batchError) {
      console.error(
        '[transactionImportService] cancelImport batch error:',
        batchError
      );

      throw batchError;
    }

    return true;
  },

  normalizeAccountType(type) {
    if (!type) {
      return '';
    }

    return (
      ACCOUNT_TYPE_MAP[
        String(type).toLowerCase()
      ] || type
    );
  },
};

export {
  transactionImportService,
};

export default transactionImportService;